import React from 'react';
import {
  Box,
  Text,
  Button,
  HStack,
  Flex,
  Icon
} from '@chakra-ui/react';
import { ContentRequirement, SocialChannel } from '@foodfluence/shared';
import { getChannelDisplayName, getChannelIcon } from '../../../utils/channelUtils';
import { getContentDurationText, hasContentGuidelines, isVideoRequirement } from '../../../utils/contentUtils';
import { ff } from '../../../theme';

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Flex justify="space-between" gap="12px" py="12px" borderBottom="1px solid" borderColor={ff.line} fontSize="14px" lineHeight={1.4}>
    <Text as="span" color={ff.text2} flex="none">{label}</Text>
    <Box as="span" fontWeight={600} color={ff.ink} textAlign="right" minW={0} wordBreak="break-word">{children}</Box>
  </Flex>
);

interface ContentRequirementsProps {
  contentRequirement: ContentRequirement;
  onViewGuidelines: () => void;
}

export const ContentRequirements: React.FC<ContentRequirementsProps> = ({
  contentRequirement,
  onViewGuidelines
}) => {
  const hasGuidelines = hasContentGuidelines(contentRequirement);

  return (
    <Box w="100%" p="16px" borderRadius="20px" bg={ff.grey}>
      <HStack justify="space-between" align="center" mb="4px" minH="44px">
        <Text fontSize="12px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color={ff.text2}>
          Content requirements
        </Text>
        {hasGuidelines ? (
          <Button
            variant="inkOutline"
            h="44px"
            px="16px"
            fontSize="14px"
            bg="white"
            onClick={onViewGuidelines}
          >
            View guidelines
          </Button>
        ) : (
          <Text fontSize="13px" color={ff.muted}>
            No guidelines available
          </Text>
        )}
      </HStack>
      <Box borderTop="1px solid" borderColor={ff.line}>
        {isVideoRequirement(contentRequirement) && (
          <Row label="Duration">
            {getContentDurationText(contentRequirement.duration) || 'Not specified'}
          </Row>
        )}
        {contentRequirement.channels && (
          <Row label="Platforms">
            {contentRequirement.channels?.length > 0 ?
              <HStack as="span" spacing="10px" display="inline-flex" flexWrap="wrap" justify="flex-end">
                {contentRequirement.channels.map((channel: SocialChannel) => {
                  const ChannelIcon = getChannelIcon(channel);
                  return (
                    <HStack as="span" key={channel} spacing="4px" display="inline-flex" alignItems="center">
                      <Icon as={ChannelIcon} boxSize="14px" color={ff.ink} />
                      <Text as="span">{getChannelDisplayName(channel)}</Text>
                    </HStack>
                  );
                })}
              </HStack>
              : 'No platforms specified'
            }
          </Row>
        )}
        {contentRequirement.additionalInformation && (
          <Text pt="12px" fontSize="15px" lineHeight={1.5} color={ff.body} whiteSpace="pre-wrap">
            {contentRequirement.additionalInformation}
          </Text>
        )}
      </Box>
    </Box>
  );
};